import React from 'react';
import { Plus, X } from 'lucide-react';
import { ConditionalLogic, FormField } from '../../../types/form';

interface ConditionalLogicBuilderProps {
  field: FormField;
  allFields: FormField[];
  onUpdate: (field: FormField) => void;
}

export const ConditionalLogicBuilder: React.FC<ConditionalLogicBuilderProps> = ({
  field,
  allFields,
  onUpdate,
}) => {
  const conditions = field.conditionalLogic || [];

  // Get fields that can be used for conditions (exclude current field and calculated fields)
  const availableFields = allFields.filter(
    (f) => f.id !== field.id && f.type !== 'calculated' && f.name
  );

  const addCondition = () => {
    const newCondition: ConditionalLogic = {
      field: '',
      operator: 'equals',
      value: '',
      action: 'show',
    };
    onUpdate({
      ...field,
      conditionalLogic: [...conditions, newCondition],
    });
  };

  const updateCondition = (index: number, updates: Partial<ConditionalLogic>) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], ...updates };
    onUpdate({ ...field, conditionalLogic: updated });
  };

  const removeCondition = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index);
    onUpdate({ ...field, conditionalLogic: updated.length > 0 ? updated : undefined });
  };

  if (availableFields.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic">
        Add other fields first to enable conditional logic
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-medium text-gray-600">
          Conditional Logic
        </label>
        <button
          type="button"
          onClick={addCondition}
          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Add Condition
        </button>
      </div>

      {conditions.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No conditions set. Field is always visible.
        </p>
      ) : (
        <div className="space-y-2">
          {conditions.map((condition, index) => (
            <div key={index} className="p-3 bg-white rounded border border-gray-200 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Condition {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeCondition(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Action</label>
                  <select
                    value={condition.action}
                    onChange={(e) => updateCondition(index, { action: e.target.value as 'show' | 'hide' })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  >
                    <option value="show">Show</option>
                    <option value="hide">Hide</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">When Field</label>
                  <select
                    value={condition.field}
                    onChange={(e) => updateCondition(index, { field: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  >
                    <option value="">Select field</option>
                    {availableFields.map((f) => (
                      <option key={f.id} value={f.name}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Operator</label>
                  <select
                    value={condition.operator}
                    onChange={(e) => updateCondition(index, { operator: e.target.value as any })}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="contains">Contains</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                    <option value="is_empty">Is Empty</option>
                    <option value="is_not_empty">Is Not Empty</option>
                  </select>
                </div>

                {condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Value</label>
                    <input
                      type="text"
                      value={condition.value}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                      placeholder="Comparison value"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
